import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminApi } from "./api";
import { formatINR, shortDate } from "./format";
import { ArrowUpRight, Sparkles, Users, FileText, Package, Briefcase } from "lucide-react";

function StatCard({ label, value, hint, accent, testid }) {
  return (
    <div className="ax-glass p-6 flex flex-col gap-2" data-testid={testid}>
      <div className="text-[10.5px] tracking-[0.18em] uppercase" style={{ color: "var(--ax-text-muted)" }}>
        {label}
      </div>
      <div className="ax-stat-num" style={{ color: accent || "var(--ax-text)" }}>
        {value}
      </div>
      {hint && (
        <div className="text-xs" style={{ color: "var(--ax-text-dim)" }}>
          {hint}
        </div>
      )}
    </div>
  );
}

const STAGE_CHIP = {
  new: "ax-chip ax-chip-info",
  contacted: "ax-chip",
  qualified: "ax-chip ax-chip-warn",
  proposal: "ax-chip ax-chip-gold",
  won: "ax-chip ax-chip-success",
  lost: "ax-chip ax-chip-danger",
};

const QUOTE_CHIP = {
  draft: "ax-chip",
  sent: "ax-chip ax-chip-info",
  accepted: "ax-chip ax-chip-success",
  rejected: "ax-chip ax-chip-danger",
  expired: "ax-chip ax-chip-warn",
  revised: "ax-chip ax-chip-gold",
  converted: "ax-chip ax-chip-success",
};

export default function DashboardOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let stop = false;
    (async () => {
      try {
        const r = await adminApi.get("/stats/overview");
        if (!stop) setData(r.data);
      } catch (e) {
        if (!stop) setErr(e?.response?.data?.detail || "Could not load stats");
      } finally {
        if (!stop) setLoading(false);
      }
    })();
    return () => {
      stop = true;
    };
  }, []);

  return (
    <div className="p-10 max-w-[1320px] mx-auto" data-testid="dashboard-overview">
      <header className="flex items-end justify-between mb-10">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-3.5 h-3.5" style={{ color: "var(--ax-gold)" }} />
            <span className="text-[10.5px] tracking-[0.32em] uppercase" style={{ color: "var(--ax-gold)" }}>
              Studio Overview
            </span>
          </div>
          <h1 className="ax-serif text-5xl tracking-[-0.02em]" style={{ color: "var(--ax-text)" }}>
            Today at <span className="ax-gold-text">GenieX</span>
          </h1>
          <p className="text-sm mt-3" style={{ color: "var(--ax-text-dim)" }}>
            A bird&apos;s-eye view of leads, quotations, inventory and active installations.
          </p>
        </div>
        <div className="hidden md:block text-[10.5px] tracking-[0.18em] uppercase" style={{ color: "var(--ax-text-muted)" }}>
          {shortDate(new Date().toISOString())}
        </div>
      </header>

      {loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="ax-glass p-6 h-[140px] ax-shimmer" />
          ))}
        </div>
      )}

      {err && !loading && (
        <div className="ax-glass p-6 mb-10 text-sm" data-testid="dashboard-error" style={{ color: "#f08e8e" }}>
          {err}
        </div>
      )}

      {data && !loading && (
        <>
          <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
            <StatCard
              testid="stat-leads"
              label="Leads"
              value={data.leads.total}
              hint={`${data.leads.new} new · ${data.leads.won} won`}
            />
            <StatCard
              testid="stat-quotations"
              label="Quotations"
              value={data.quotations.total}
              hint={`${data.quotations.accepted} accepted`}
              accent="var(--ax-gold)"
            />
            <StatCard
              testid="stat-pipeline"
              label="Pipeline Value"
              value={formatINR(data.quotations.pipeline_value || 0)}
              hint="Sent + accepted"
              accent="var(--ax-gold)"
            />
            <StatCard
              testid="stat-products"
              label="Catalog"
              value={data.products.total}
              hint={`${data.projects.active} active projects`}
            />
          </section>

          <section className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Recent quotations */}
            <div className="ax-glass p-6" data-testid="recent-quotations">
              <div className="flex items-center justify-between mb-5">
                <h3 className="ax-serif text-2xl">Recent quotations</h3>
                <Link to="/admin-dashboard/quotations" className="text-xs tracking-[0.18em] uppercase flex items-center gap-1" style={{ color: "var(--ax-gold)" }}>
                  View all <ArrowUpRight className="w-3.5 h-3.5" />
                </Link>
              </div>
              {(data.recent_quotes || []).length === 0 && (
                <div className="text-sm py-8 text-center" style={{ color: "var(--ax-text-muted)" }}>
                  No quotations yet.
                </div>
              )}
              <div className="space-y-2">
                {(data.recent_quotes || []).map((q) => (
                  <Link
                    key={q.id}
                    to={`/admin-dashboard/quotations/${q.id}`}
                    className="flex items-center justify-between gap-3 px-3 py-3 rounded-lg hover:bg-white/[0.03] transition-colors"
                    data-testid={`recent-quote-${q.id}`}
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{q.client_name || "Untitled"}</div>
                      <div className="text-xs mt-0.5" style={{ color: "var(--ax-text-muted)" }}>
                        {q.quote_number || "—"} · {shortDate(q.created_at)}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <div className="ax-mono text-sm" style={{ color: "var(--ax-gold)" }}>
                        {formatINR(q.grand_total || 0)}
                      </div>
                      <span className={QUOTE_CHIP[q.status] || "ax-chip"}>{q.status || "draft"}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Recent leads */}
            <div className="ax-glass p-6" data-testid="recent-leads">
              <div className="flex items-center justify-between mb-5">
                <h3 className="ax-serif text-2xl">Recent leads</h3>
                <Link to="/admin-dashboard/crm" className="text-xs tracking-[0.18em] uppercase flex items-center gap-1" style={{ color: "var(--ax-gold)" }}>
                  View all <ArrowUpRight className="w-3.5 h-3.5" />
                </Link>
              </div>
              {(data.recent_leads || []).length === 0 && (
                <div className="text-sm py-8 text-center" style={{ color: "var(--ax-text-muted)" }}>
                  No leads yet.
                </div>
              )}
              <div className="space-y-2">
                {(data.recent_leads || []).map((l) => (
                  <div
                    key={l.id}
                    className="flex items-center justify-between gap-3 px-3 py-3 rounded-lg hover:bg-white/[0.03] transition-colors"
                    data-testid={`recent-lead-${l.id}`}
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{l.name}</div>
                      <div className="text-xs mt-0.5" style={{ color: "var(--ax-text-muted)" }}>
                        {l.phone || l.email || "—"} · {l.city || "—"}
                      </div>
                    </div>
                    <span className={STAGE_CHIP[l.stage] || "ax-chip"}>{l.stage || "new"}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
            <Link to="/admin-dashboard/crm" className="ax-glass p-4 flex items-center gap-3" data-testid="quick-leads">
              <Users className="w-5 h-5" style={{ color: "var(--ax-gold)" }} />
              <span className="text-sm">Manage leads</span>
            </Link>
            <Link to="/admin-dashboard/quotations/new" className="ax-glass p-4 flex items-center gap-3" data-testid="quick-new-quote">
              <FileText className="w-5 h-5" style={{ color: "var(--ax-gold)" }} />
              <span className="text-sm">New quotation</span>
            </Link>
            <Link to="/admin-dashboard/inventory" className="ax-glass p-4 flex items-center gap-3" data-testid="quick-inventory">
              <Package className="w-5 h-5" style={{ color: "var(--ax-gold)" }} />
              <span className="text-sm">Add product</span>
            </Link>
            <Link to="/admin-dashboard/projects" className="ax-glass p-4 flex items-center gap-3" data-testid="quick-projects">
              <Briefcase className="w-5 h-5" style={{ color: "var(--ax-gold)" }} />
              <span className="text-sm">Projects</span>
            </Link>
          </section>
        </>
      )}
    </div>
  );
}
